/* eslint-disable max-lines */
import { UntypedFormGroup } from '@angular/forms';

import { BillingPersonType } from '@booking/entities/billing-person-types';
import { TravelerTypes } from '@pax/entities/traveler-info';
import {
  BookingPaxTypeDocumentProps, BookingBillingTypeDocumentProps, BookingDocumentError,
  BookingPaxDocumentValidations, BookingBillingDocumentValidations
} from '@app/entities/booking-documents';
import { PERSON_FORM_INPUTS, TRAVELER_FORM_INPUTS } from '@booking/constants/forms.constants';
import { User } from '@app/modules/core/interfaces/user';
import { TravelerPaxResponse } from '@pax/entities/traveler-pax';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { InputMask, InputRegex } from '@forms/form-text-input/entities/input-mask';
import { FormDocumentInputTemplate } from '@forms/form-text-input/entities/input-type';
import { BillingResponse } from '@billing/entities/billing.payload';
import { BookingDocumentPaxIdType } from '@app/entities/booking-pax-documents-id';
import { BookingDocumentBillingIdType } from '@app/entities/booking-billing-documents-id';

const DEFAULT_PAX_VALIDATIONS: BookingPaxDocumentValidations = {
  MAX_LENGTH: 15,
  MASK: InputMask.DIGITS,
  REGEX: InputRegex.NUMERIC,
  FORM_INPUT: FormDocumentInputTemplate.DIGITS,
  IS_DISABLED: false,
  DOCUMENT_BUSINESS_RULES_BY_MODULES: {
    [TravelBookingType.FLIGHTS]: {
      NATIONAL_TRIP: true,
      INTERNATIONAL_TRIP: true,
      ONLY_NATIONAL_CITIZENS: false,
    },
    [TravelBookingType.FLIGHTS_BY_SECTOR]: {
      NATIONAL_TRIP: true,
      INTERNATIONAL_TRIP: true,
      ONLY_NATIONAL_CITIZENS: false,
    },
    [TravelBookingType.HOTELS]: {
      NATIONAL_TRIP: true,
      INTERNATIONAL_TRIP: true,
      ONLY_NATIONAL_CITIZENS: false,
    },
    [TravelBookingType.BUSES]: {
      NATIONAL_TRIP: true,
      INTERNATIONAL_TRIP: true,
      ONLY_NATIONAL_CITIZENS: false,
    },
    [TravelBookingType.AUTOS]: {
      NATIONAL_TRIP: true,
      INTERNATIONAL_TRIP: true,
      ONLY_NATIONAL_CITIZENS: false,
    },
    [TravelBookingType.PACKAGE_AIR]: {
      NATIONAL_TRIP: true,
      INTERNATIONAL_TRIP: true,
      ONLY_NATIONAL_CITIZENS: false,
    },
    [TravelBookingType.ACTIVITIES]: {
      NATIONAL_TRIP: true,
      INTERNATIONAL_TRIP: true,
      ONLY_NATIONAL_CITIZENS: false,
    },
  }
};

const DEFAULT_BILLING_VALIDATIONS: BookingBillingDocumentValidations = {
  MAX_LENGTH: 15,
  MASK: InputMask.DIGITS,
  REGEX: InputRegex.NUMERIC,
  FORM_INPUT: FormDocumentInputTemplate.DIGITS,
  IS_DISABLED: false,
  DOCUMENT_BUSINESS_RULES_BY_MODULES: {
    [TravelBookingType.FLIGHTS]: {
      LOCAL_NATIONALITY: true,
      FOREIGN_NATIONALITY: true,
      FOREIGN_TRAVELER: true,
    },
    [TravelBookingType.FLIGHTS_BY_SECTOR]: {
      LOCAL_NATIONALITY: true,
      FOREIGN_NATIONALITY: true,
      FOREIGN_TRAVELER: true,
    },
    [TravelBookingType.HOTELS]: {
      LOCAL_NATIONALITY: true,
      FOREIGN_NATIONALITY: true,
      FOREIGN_TRAVELER: true,
    },
    [TravelBookingType.BUSES]: {
      LOCAL_NATIONALITY: true,
      FOREIGN_NATIONALITY: true,
      FOREIGN_TRAVELER: true,
    },
    [TravelBookingType.AUTOS]: {
      LOCAL_NATIONALITY: true,
      FOREIGN_NATIONALITY: true,
      FOREIGN_TRAVELER: true,
    },
    [TravelBookingType.PACKAGE_AIR]: {
      LOCAL_NATIONALITY: true,
      FOREIGN_NATIONALITY: true,
      FOREIGN_TRAVELER: true,
    },
    [TravelBookingType.ACTIVITIES]: {
      LOCAL_NATIONALITY: true,
      FOREIGN_NATIONALITY: true,
      FOREIGN_TRAVELER: true,
    },
  }
};


export function getDocumentCustomErrors(
  formType: BillingPersonType | TravelerTypes,
  documentType: BookingDocumentPaxIdType | BookingDocumentBillingIdType,
  documents: BookingPaxTypeDocumentProps[] | BookingBillingTypeDocumentProps[]
): BookingDocumentError[] {
  switch (formType) {
    case BillingPersonType.JURIDICAL_PERSON:
    case BillingPersonType.NATURAL_PERSON:
      const _documentsBilling = documents as BookingBillingTypeDocumentProps[];
      const _documentTypeBilling = documentType as BookingDocumentBillingIdType;
      const documentBilling = _documentsBilling.find(_doc => _doc.TYPE === _documentTypeBilling);
      return documentBilling ? documentBilling.ERRORS : null;
    case TravelerTypes.ADULTS:
    case TravelerTypes.CHILDREN:
    case TravelerTypes.INFANTS:
      const _documentsPax = documents as BookingPaxTypeDocumentProps[];
      const _documentTypePax = documentType as BookingDocumentPaxIdType;
      const documentPax = _documentsPax.find(_doc => _doc.TYPE === _documentTypePax);
      return documentPax ? documentPax.ERRORS : null;
    default:
      return null;
  }
}

export function documentInputPlaceholder(
  formType: BillingPersonType | TravelerTypes,
  documentType: BookingDocumentPaxIdType | BookingDocumentBillingIdType,
  isOnlyOne: boolean
): string {
  switch (formType) {
    case BillingPersonType.JURIDICAL_PERSON:
    case BillingPersonType.NATURAL_PERSON:
      const _documentTypeBilling = documentType as BookingDocumentBillingIdType;
      return _documentTypeBilling === BookingDocumentBillingIdType.NIT ?
        'BOOKING_MODULE.BILLING.NIT_EXAMPLE' : isOnlyOne ? '' : 'BOOKING_MODULE.DOCUMENT_NUMBER';
    default:
      return '';
  }
}

export function travelerFormDocumentType(
  form: UntypedFormGroup, documents: BookingPaxTypeDocumentProps[]): BookingDocumentPaxIdType {
  const documentId = form?.get(TRAVELER_FORM_INPUTS.DOCUMENT_ID)?.value;
  const document = documents.find(_doc => _doc.DOCUMENT_ID === documentId);
  return document ? document.TYPE : null;
}

export function travelerFormDocumentValidators(
  form: UntypedFormGroup, documents: BookingPaxTypeDocumentProps[]
): BookingPaxDocumentValidations {
  const documentId = form?.get(TRAVELER_FORM_INPUTS.DOCUMENT_ID)?.value;
  const document = documents.find(_doc => _doc.DOCUMENT_ID === documentId);
  return document && document?.VALIDATIONS ? document.VALIDATIONS : DEFAULT_PAX_VALIDATIONS as BookingPaxDocumentValidations;
}

export function internationalPaxDocumentFilledOut(documentType: BookingDocumentPaxIdType, isInternationalTrip: boolean): boolean {
  return !!documentType && documentType === BookingDocumentPaxIdType.PAS && isInternationalTrip;
}

export function defaultPaxDocument(
  documents: BookingPaxTypeDocumentProps[], hasPaxTravelerInfo: boolean,
  hasUserInfo: boolean, form: UntypedFormGroup, user: User
): string {
  let document: BookingPaxTypeDocumentProps = null;
  if (hasPaxTravelerInfo) {
    const documentId = form?.get(TRAVELER_FORM_INPUTS.DOCUMENT_ID)?.value;
    return getPaxDocumentNameById(documents, documentId);
  } else if (hasUserInfo) {
    document = documents.find(doc => doc.RAPPI_DOCUMENT_ID === user.identification_type);
    return document ? document.DOCUMENT_NAME : (documents.length === 1) ? documents[0].DOCUMENT_NAME : null;
  } else {
    return (documents?.length === 1) ? documents[0].DOCUMENT_NAME : null;
  }
}

export function getPaxDocumentNameById(documents: BookingPaxTypeDocumentProps[], documentId: number): string {
  if (documents.length === 1) {
    return documents[0].DOCUMENT_NAME;
  } else {
    const document = documents.find(doc => doc.DOCUMENT_ID === documentId);
    return document ? document.DOCUMENT_NAME : null;
  }
}

export function getPaxDocumentAbreviationById(documents: BookingPaxTypeDocumentProps[], documentId: number): string {
  const document = documents.find(doc => doc.DOCUMENT_ID === documentId);
  return document ? document.ABREVIATION : null;
}

export function getPaxDocumentTypeById(documents: BookingPaxTypeDocumentProps[], documentId: number): BookingDocumentPaxIdType {
  const document = documents.find(doc => doc.DOCUMENT_ID === documentId);
  return document ? document.TYPE : null;
}

export function getPaxDocumentIdByName(
  documentName: string,
  formType: BillingPersonType | TravelerTypes,
  documents: BookingPaxTypeDocumentProps[] | BookingBillingTypeDocumentProps[]
): number {
  switch (formType) {
    case BillingPersonType.JURIDICAL_PERSON:
    case BillingPersonType.NATURAL_PERSON:
      const _documentsBilling = documents as BookingBillingTypeDocumentProps[];
      const documentBilling = _documentsBilling.find(_doc => _doc.DOCUMENT_NAME === documentName);
      return documentBilling ? documentBilling.DOCUMENT_ID : null;
    case TravelerTypes.ADULTS:
    case TravelerTypes.CHILDREN:
    case TravelerTypes.INFANTS:
      const _documentsPax = documents as BookingPaxTypeDocumentProps[];
      const documentPax = _documentsPax.find(_doc => _doc.DOCUMENT_NAME === documentName);
      return documentPax ? documentPax.DOCUMENT_ID : null;
    default:
      return null;
  }
}

export function internationalPaxDocumentFilledOutWithAbreviation(
  abreviation: string, isInternationalTrip: boolean): boolean {
  return !!abreviation && abreviation === (BookingDocumentPaxIdType.PAS as string) && isInternationalTrip;
}

export function defaultPaxDocumentId(
  documents: BookingPaxTypeDocumentProps[], hasPaxTravelerInfo: boolean,
  hasUserInfo: boolean, paxTraveler: TravelerPaxResponse, user: User
): number {
  const dataInfo = hasPaxTravelerInfo ? paxTraveler : hasUserInfo ? user : {};
  const documentId = hasPaxTravelerInfo ?
    (dataInfo as TravelerPaxResponse).configuration_document_id :
    (dataInfo as User).identification_type;
  if (documents.length === 1 && !hasPaxTravelerInfo) return documents[0].DOCUMENT_ID;
  else {
    const document = hasPaxTravelerInfo ? documents.find(doc => doc.DOCUMENT_ID === documentId) : hasUserInfo ?
      documents.find(doc => doc.RAPPI_DOCUMENT_ID === documentId || doc.DOCUMENT_ID === documentId) : null;
    return document ? document.DOCUMENT_ID : null;
  }
}

export function defaultBillingDocumentId(
  documents: BookingBillingTypeDocumentProps[], user: User, billing: BillingResponse,
  pax: TravelerPaxResponse, hasDefaultBillingInfo: boolean, hasUserInfo: boolean,
  hasPaxSelection: boolean
): number {
  const dataInfo = hasDefaultBillingInfo ? billing : hasUserInfo ? user : null;
  if (dataInfo) {
    if (documents.length === 1) return documents[0].DOCUMENT_ID;
    else {
      let document = null;
      const documentId = hasDefaultBillingInfo ?
        (dataInfo as BillingResponse).billing_info.configuration_document_id :
        (dataInfo as User).identification_type;
      if (hasDefaultBillingInfo) {
        document = documents.find(doc => doc.DOCUMENT_ID === documentId);
        return document ? document.DOCUMENT_ID : null;
      } else if (hasPaxSelection) {
        document = documents.find(doc => doc.DOCUMENT_ID === pax.configuration_document_id);
        return document ? document.DOCUMENT_ID : null;
      } else {
        document = documents.find(doc => doc.RAPPI_DOCUMENT_ID === documentId);
        return document ? document.DOCUMENT_ID : null;
      }
    }
  } else return null;
}

export function defaultBillingDocumentNumber(
  documents: BookingBillingTypeDocumentProps[], user: User, billing: BillingResponse, pax: TravelerPaxResponse,
  hasDefaultBillingInfo: boolean, hasPaxSelection: boolean, hasUserInfo: boolean
): string {
  const dataInfo = hasDefaultBillingInfo ? billing : hasUserInfo ? user : null;
  if (dataInfo) {
    let document = null;
    const documentId = hasDefaultBillingInfo ?
      (dataInfo as BillingResponse).billing_info.configuration_document_id :
      (dataInfo as User).identification_type;
    if (hasDefaultBillingInfo) {
      document = documents.find(doc => doc.DOCUMENT_ID === documentId);
      return document ? billing.billing_info.document_number : null;
    } else if (hasPaxSelection) {
      document = documents.find(doc => doc.DOCUMENT_ID === pax.configuration_document_id);
      return document ? pax.document_number : null;
    } else {
      document = documents.find(doc => doc.RAPPI_DOCUMENT_ID === documentId);
      return document ? user.identification : null;
    }
  } else return null;
}

export function billingFormDocumentType(
  form: UntypedFormGroup, documents: BookingBillingTypeDocumentProps[]
): BookingDocumentBillingIdType {
  const documentId = form?.get(PERSON_FORM_INPUTS.DOCUMENT_ID)?.value;
  const document = documents.find(_doc => _doc.DOCUMENT_ID === documentId);
  return document ? document.TYPE : null;
}

export function defaultBillingDocumentByForm(
  documents: BookingBillingTypeDocumentProps[], form: UntypedFormGroup
): string {
  const documentId = form?.get(PERSON_FORM_INPUTS.DOCUMENT_ID)?.value;
  return getBillingDocumentNameById(documents, documentId);
}

export function billingFormDocumentValidators(
  form: UntypedFormGroup, documents: BookingBillingTypeDocumentProps[]
): BookingBillingDocumentValidations {
  const documentId = form?.get(PERSON_FORM_INPUTS.DOCUMENT_ID)?.value;
  const document = documents.find(_doc => _doc.DOCUMENT_ID === documentId);
  return document && document?.VALIDATIONS ? document.VALIDATIONS : DEFAULT_BILLING_VALIDATIONS;
}

export function defaultBillingDocumentAbreviationByInfo(
  documents: BookingBillingTypeDocumentProps[], billing: BillingResponse
): string {
  return getBillingDocumentAbreviationById(documents, billing.billing_info.configuration_document_id);
}

export function getBillingPersonType(isNaturalPerson: boolean): BillingPersonType {
  return isNaturalPerson ? BillingPersonType.NATURAL_PERSON : BillingPersonType.JURIDICAL_PERSON;
}

export function getBillingDocumentNameById(documents: BookingBillingTypeDocumentProps[], documentId: number): string {
  if (documents.length === 1) {
    return documents[0].DOCUMENT_NAME;
  } else {
    const document = documents.find(doc => doc.DOCUMENT_ID === documentId);
    return document ? document.DOCUMENT_NAME : null;
  }
}

function getBillingDocumentAbreviationById(documents: BookingBillingTypeDocumentProps[], documentId: number): string {
  const document = documents.find(doc => doc.DOCUMENT_ID === documentId);
  return document ? document.ABREVIATION : null;
}

export const exportedForTesting = {
  DEFAULT_PAX_VALIDATIONS,
  DEFAULT_BILLING_VALIDATIONS
}
